import type { UiState, ViewSDK } from '@salesforce/platform-sdk/view';

import { queryClient } from '@api';
import { Layout } from '@layouts';
import { Notifications } from '@mantine/notifications';
import { getViewSDK } from '@salesforce/platform-sdk/view';
import { useAccountStore } from '@store';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { HomePage } from './pages/home/HomePage';
import { NoMatchPage } from './pages/noMatch/NoMatchPage';
import { QualityFormulasPage } from './pages/quality-formulas/QualityFormulasPage';
import { TemplateManagerProtectedRoute } from './pages/templates/components/TemplateManagerProtectedRoute';
import { TemplateEditorPage } from './pages/templates/TemplateEditorPage';
import { TemplateManagerPage } from './pages/templates/TemplateManagerPage';
import { ViewSdkContext } from './ViewSdkContext';

interface ContractEntryUiState extends UiState {
  props: { accountId?: string; accountName?: string; availableEmbedHeight?: number };
}

export const App = () => {
  const [viewSdk, setViewSdk] = useState<ViewSDK>();
  const [availableEmbedHeight, setAvailableEmbedHeight] = useState<number>();
  const setAccountId = useAccountStore(state => state.setAccountId);
  const setAccountName = useAccountStore(state => state.setAccountName);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void getViewSDK().then(sdk => {
      if (cancelled) return;
      const uiState = sdk.getUiState?.<ContractEntryUiState>();
      // If absent, start a new contract with no preselected Account.
      setAccountId(uiState?.state.props.accountId);
      // The host resolves this via lightning/uiRecordApi under the running user's own access, not a guest-side fetch.
      setAccountName(uiState?.state.props.accountName);
      setAvailableEmbedHeight(uiState?.state.props.availableEmbedHeight);
      unsubscribe = uiState?.subscribe(next => {
        setAccountId(next.props.accountId);
        setAccountName(next.props.accountName);
      });
      setViewSdk(sdk);
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [setAccountId, setAccountName]);

  useEffect(() => {
    if (!viewSdk) return;
    const handleHeightChange = (event: Event) => {
      const nextHeight = (event as CustomEvent<{ availableEmbedHeight: number }>).detail.availableEmbedHeight;
      if (typeof nextHeight === 'number' && Number.isFinite(nextHeight) && nextHeight >= 0) {
        setAvailableEmbedHeight(nextHeight);
      }
    };
    viewSdk.addEventListener?.('viewport-height-change', handleHeightChange);

    viewSdk.dispatchEvent?.(new CustomEvent('app-ready'));
    return () => viewSdk.removeEventListener?.('viewport-height-change', handleHeightChange);
  }, [viewSdk]);

  if (!viewSdk) {
    return null;
  }

  return (
    <ViewSdkContext value={viewSdk}>
      <QueryClientProvider client={queryClient}>
        <Notifications position="top-right" />
        <BrowserRouter>
          <Routes>
            <Route element={<Layout availableEmbedHeight={availableEmbedHeight} />} path="/">
              <Route element={<HomePage />} path="/" />
              {/* Quality Calcs, ported as-is from the Pricing Engine reference app (still Mantine-styled
                  for now) — see pages/quality-formulas and pages/templates. */}
              <Route element={<QualityFormulasPage />} path="quality-formulas" />
              <Route element={<TemplateManagerPage />} path="templates" />
              <Route element={<TemplateEditorPage />} path="templates/:id/edit" />
              <Route element={<TemplateManagerProtectedRoute />}>
                <Route element={<TemplateEditorPage />} path="templates/new" />
              </Route>
              {/* Catch-all */}
              <Route element={<NoMatchPage />} path="*" />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ViewSdkContext>
  );
};
